import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import FeedbackForm from '@/pages/about/components/FeedbackForm';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function AboutPage() {
  useDocumentTitle(
    '关于我们 - 靠逼视频',
    '了解靠逼视频的平台定位、内容覆盖范围、联系方式以及使用与隐私保护声明。',
  );

  const totalViews = videos.reduce((sum, v) => sum + v.views, 0);

  return (
    <div className="animate-fade-up">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-12">
        <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-600 mb-5" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">关于我们</span>
        </nav>

        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/15 text-primary-400 text-xs font-medium">
          <i className="ri-information-line"></i>
          关于靠逼视频
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 max-w-3xl leading-tight">
          让每一次在线观看都轻松、丰富又愉快
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
          靠逼视频是一个专注高清视频内容的在线观看与分享平台，面向喜爱在线观看视频的年轻用户与娱乐爱好者，致力于提供丰富多样的内容与便捷的操作体验。
        </p>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: '内容分类', value: `${categories.length} 个` },
            { label: '演示视频', value: `${videos.length} 部` },
            { label: '累计播放', value: `${(totalViews / 100000000).toFixed(2)} 亿` },
            { label: '注册门槛', value: '无需登录' },
          ].map((s) => (
            <div key={s.label} className="bg-background-100 border border-background-200 rounded-lg p-5">
              <p className="font-heading text-xl md:text-2xl font-bold text-accent-400">{s.value}</p>
              <p className="mt-1 text-xs md:text-sm text-foreground-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">平台定位</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                我们相信，好内容值得被更多人轻松看到。靠逼视频以「聚合 + 分享」为核心，把散落在不同主题下的优质视频整理成清晰的分类，让用户可以快速找到感兴趣的内容，在线流畅观看，并随手分享给身边的人。
              </p>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                页面设计采用现代门户式布局，结合电影海报式的视觉元素，兼顾浏览的舒适度与内容的展示效果。无论是想放松一下，还是想发现新鲜事物，都能在这里找到属于你的那一段视频。
              </p>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">内容覆盖范围</h2>
              <p className="mt-2 text-sm text-foreground-600">当前涵盖以下主流视频类别，内容持续更新中。</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-400 transition-colors"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400 text-xl shrink-0">
                      <i className={c.icon}></i>
                    </span>
                    <span>
                      <span className="block font-heading text-sm font-semibold text-foreground-950">{c.name}视频</span>
                      <span className="block mt-1 text-xs text-foreground-600 leading-relaxed">{c.description}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">使用说明与隐私保护</h2>
              <ul className="mt-3 space-y-3 text-sm text-foreground-700 leading-relaxed">
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0"></i>
                  本站无需注册登录即可浏览与观看，页面简洁、操作直观。
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0"></i>
                  视频播放为前端演示，仅用于展示观看体验，暂未接入真实流媒体服务。
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0"></i>
                  分享功能仅复制当前详情页链接到剪贴板，不涉及任何第三方账号授权。
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0"></i>
                  我们仅在反馈表单中收集你主动提交的联系方式，用于回复你的问题，不会用于其他用途。
                </li>
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="bg-secondary-100 border border-secondary-200 rounded-lg p-6">
              <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4 text-sm">
                <li className="flex items-center gap-3 text-foreground-800">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500 text-background-950 shrink-0">
                    <i className="ri-mail-send-line"></i>
                  </span>
                  hello@kaobivideo.com
                </li>
                <li className="flex items-center gap-3 text-foreground-800">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-accent-400 shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  工作日 09:00 - 18:00
                </li>
              </ul>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-lg p-6">
              <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-4">意见反馈</h2>
              <FeedbackForm />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}